// Onboarding page for Google users who don't yet have birth data
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DateInput, { ageInYears, MIN_AGE } from "../components/DateInput";

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        date_of_birth: "",
        time_of_birth: "12:00",
        birth_city: "",
        birth_country: "",
        birth_lat: "",
        birth_lng: "",
    });
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (ageInYears(form.date_of_birth) < MIN_AGE) {
            setError(`You must be at least ${MIN_AGE} to use StarPath.`);
            return;
        }
        setBusy(true); setError("");
        try {
            await api.post("/me/birth", {
                birth: {
                    ...form,
                    birth_state: form.birth_state || null,
                    birth_zip: form.birth_zip || null,
                    birth_lat: form.birth_lat ? parseFloat(form.birth_lat) : null,
                    birth_lng: form.birth_lng ? parseFloat(form.birth_lng) : null,
                },
            });
            await refresh();
            navigate("/dashboard");
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sand-50 text-ink">
            <div className="max-w-lg mx-auto px-6 pt-20 pb-24">
                <div className="sp-overline">One more step{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
                <h1 className="text-4xl sm:text-5xl font-serif mt-3">Your birth, briefly.</h1>
                <p className="text-ink-soft mt-2">We never share this — it lives only on your chart.</p>

                <form onSubmit={submit} className="mt-10 space-y-6" data-testid="onboarding-form">
                    <div>
                        <label className="sp-overline">Date of birth</label>
                        <DateInput required value={form.date_of_birth} onChange={update("date_of_birth")} className="sp-input" data-testid="onboarding-date" />
                        <p className="text-xs text-ink-muted mt-2">MM / DD / YYYY — you must be at least {MIN_AGE}.</p>
                    </div>
                    <div>
                        <label className="sp-overline">Time of birth</label>
                        <input type="time" required value={form.time_of_birth} onChange={update("time_of_birth")} className="sp-input" data-testid="onboarding-time" />
                    </div>
                    <div>
                        <label className="sp-overline">Birth city</label>
                        <input type="text" required value={form.birth_city} onChange={update("birth_city")} className="sp-input" data-testid="onboarding-city" />
                    </div>
                    <div>
                        <label className="sp-overline">Birth country</label>
                        <input type="text" required value={form.birth_country} onChange={update("birth_country")} className="sp-input" data-testid="onboarding-country" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <input type="text" placeholder="State / region (optional)" value={form.birth_state} onChange={update("birth_state")} className="sp-input text-base" data-testid="onboarding-state" />
                        <input type="text" placeholder="Zip / postal (optional)" value={form.birth_zip} onChange={update("birth_zip")} className="sp-input text-base" data-testid="onboarding-zip" />
                    </div>
                    <p className="text-xs text-ink-muted -mt-2">We&apos;ll auto-locate your coordinates from the city you provide.</p>
                    {error && <div className="text-sm text-red-700" data-testid="onboarding-error">{error}</div>}
                    <button type="submit" disabled={busy} className="sp-btn-primary" data-testid="onboarding-submit">
                        {busy ? "Drawing your chart…" : "Reveal my chart"}
                    </button>
                </form>
            </div>
        </div>
    );
}
